<script>

import config from '@/common/genericPanelConf';
import Service from '@/services/stock-management';
import GenericService from '@/services/genericService.js';
import CommonConstants from '@/common/Constants';
import { minValue, required  } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import Utils from '@/common/Utils';
import ResponsiveButton from '@/components/generic/ResponsiveButton.vue';
import GenericCustomMasterDataAutocompleteV2 from '@/components/generic/GenericCustomMasterDataAutocompleteV2.vue';
import GenericCustomAutocompleteV2 from '@/components/generic/GenericCustomAutocompleteV2.vue';
import GenericDataTable from '@/components/generic/GenericDataTable.vue';

export default {
    setup: () => ({ v$: useVuelidate() }),
    components: {
        ResponsiveButton,
        GenericCustomMasterDataAutocompleteV2,
        GenericCustomAutocompleteV2,
        GenericDataTable
    },
    mounted() {
        this.$store.dispatch('security/setNavbarTitle', 'StockManagementMts.page_title');
    },
    created() {
        this.configuration = config.getConfiguration('StockManagementMts');
    },
    data() {
        return {
            configuration: {},
            filters: {
                referenceDate: Utils.formatDatePlusDays(new Date(),-1),
            },
            twentyFour: '24.00',
            onAddMtsDataItemVisible: false,
            newItem: {},
            submitted: false,
            newItemSubmitted: false
        }
    },
    validations() {
        return {
            filters: {
                referenceDate: {required}
            },
            newItem: {
                dailyStockReferenceDate: {required},
                idRegistryPlant: {required},
                measureDate: {required},
                tankCode: {required},
                waterLevel: {
                    required, 
                    minValue: minValue(0)
                }
            }
        }
    },
    computed: {
        constants(){
            return CommonConstants;
        },
        utils(){
            return Utils;
        },
        uiPermissions(){
            return this.$store.getters['security/uiPermissions'];
        },
    },
    methods: {
        loadGrid() {
            this.submitted = true;
            if(!this.v$.filters.$invalid){
                let filters = this.prepareFilters();
                this.$refs.mtsGrid.filterFromView(filters, true);
            }
        },
        prepareFilters() {
            let filters = {
                referenceDate: Utils.formatDateForAPI(Utils.formatDatePlusDays(this.filters.referenceDate, 1)),
            };

            let cacheData = {
                name: 'StockManagementMts',
                filters: filters
            };
            this.$store.dispatch("cacheDataStore/setGridCache", cacheData);

            return filters;
        },
        reimport() {
            if(this.v$.filters.$invalid) {
                this.$toast.add({detail: this.$t('StockManagementMts.message.referenceDateRequired'), life:3000, severity:'error'});
                return;
            }
            let referenceDate = Utils.formatDateForAPI(Utils.formatDatePlusDays(this.filters.referenceDate, 1));
            this.$confirm.require({
                        group: 'confirmDialog',
                        message: this.$t('StockManagementMts.message.confirmReimport'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: this.$t('yes'),
                        rejectLabel: this.$t('no'),
                        accept: () => {
                            this.$store.dispatch("generic/setLoading", true);
                            Service.importMts(referenceDate, response => {
                                this.$store.dispatch("generic/setLoading", false);
                                if(response.success){
                                    this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000});
                                    this.addOperationDialogVisible = false;
                                    this.loadGrid();
                                }else{
                                    this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                                    this.$confirm.close();
                                }
                            });
                        },
                        reject: () => {
                            //callback to execute when user rejects the action
                            this.$confirm.close();
                        }
                    });
        },
        exportToManualDailyStock() {
            if(this.v$.filters.$invalid) {
                this.$toast.add({detail: this.$t('StockManagementMts.message.referenceDateRequired'), life:3000, severity:'error'});
                return;
            }
            let referenceDate = Utils.formatDateForAPI(Utils.formatDatePlusDays(this.filters.referenceDate, 1));
            this.$confirm.require({
                        group: 'confirmDialog',
                        message: this.$t('StockManagementMts.message.confirmExport'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: this.$t('yes'),
                        rejectLabel: this.$t('no'),
                        accept: () => {
                            this.$store.dispatch("generic/setLoading", true);
                            console.log("OK");
                            Service.exportMtsToManualDailyStock(referenceDate, response => {
                                this.$store.dispatch("generic/setLoading", false);
                                if(response.success){
                                    this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000});
                                    this.addOperationDialogVisible = false;
                                    this.loadGrid();
                                }else{
                                    this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                                    this.$confirm.close();
                                }
                            });
                        },
                        reject: () => {
                            //callback to execute when user rejects the action
                            this.$confirm.close();
                        }
                    });
        },
        onAddUpdateDataItem(){
            if(this.newItem.id) {
                this.editMtsDataItem();
            } else {
                this.addMtsDataItem();
            }
        },
        onAddMtsDataItemClick(){
            this.newItem={
                dailyStockReferenceDate: this.filters.referenceDate
            }

            this.onAddMtsDataItemVisible = true;
        },
        addMtsDataItem() {
            this.newItemSubmitted = true;
            if(this.v$.newItem.$invalid){
                return;
            }

            let request = {
                dailyStockReferenceDate: Utils.formatDateForAPI(Utils.formatDatePlusDays(this.newItem.dailyStockReferenceDate, 1)),
                idRegistryPlant: this.newItem.idRegistryPlant,
                measureDate: this.newItem.measureDate,
                tankCodeMts: this.newItem.tankCodeMts,
                tankCode: this.newItem.tankCode,
                waterLevelMts: this.newItem.waterLevelMts,
                waterLevel: this.newItem.waterLevel,
                notes: this.newItem.notes,
                productLevelMts: this.newItem.productLevelMts
            };

            this.$store.dispatch("generic/setLoading", true);
            Service.addMtsDataItem(request, response => {
                this.$store.dispatch("generic/setLoading", false);
                this.newItemSubmitted = false;
                if(response.success){
                    this.$toast.add({severity:'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000});
                    this.onAddMtsDataItemVisible = false;
                    this.loadGrid();
                } else {
                    this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                }
            });
        },
        onEditMtsDataItemClick(data){
            this.newItem = {
                ...data
            };

            this.newItem.dailyStockReferenceDate = this.filters.referenceDate;
            this.newItem.measureDate = new Date(Utils.dateToCurrentTimeZone(this.newItem.measureDate));

            this.onAddMtsDataItemVisible = true;
        },
        editMtsDataItem() {
            this.newItemSubmitted = true;
            if(this.v$.newItem.$invalid){
                return;
            }
            
            var request = {
                id: this.newItem.id,
                idRegistryPlant: this.newItem.idRegistryPlant,
                measureDate: this.newItem.measureDate,
                tankCode: this.newItem.tankCode,
                waterLevel: this.newItem.waterLevel 
            };

            this.$store.dispatch("generic/setLoading", true);
            Service.updateMtsDataItem(request, response => {
                this.$store.dispatch("generic/setLoading", false);
                this.newItemSubmitted = false;
                if(response.success){
                    this.$refs.mtsGrid.hideDialogEdit();
                    this.onAddMtsDataItemVisible = false;
                    this.loadGrid();
                } else {
                    this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                }
            });
        }
    }
}
</script>

<template>
    <main>
        <ConfirmDialog group="confirmDialog">
            <template #message="slotProps">
                <div class="flex flex-column align-items-center w-full gap-3 border-bottom-1 surface-border">
                <i :class="slotProps.message.icon" class="text-6xl text-primary-500"></i>
                <span v-html="slotProps.message.message"></span>
            </div>
            </template>
        </ConfirmDialog>


        <!-- Dialog Aggiungi/Modifica -->
        <Dialog v-model:visible="onAddMtsDataItemVisible" :modal="true" style="width:25vw;min-height: 20vh" >
            <h4>
                {{this.newItem.id != null ? $t('StockManagementMts.btn.update') : $t('StockManagementMts.btn.insert')}}
            </h4>
            <div class="grid">
                <div class="col-12">
                    <label :class="{'p-error':v$.newItem.dailyStockReferenceDate.$invalid && newItemSubmitted}">{{$t('StockManagementMts.referenceDate')}}</label>
                    <Calendar 
                        class="w-full" 
                        :class="{'p-invalid':v$.newItem.dailyStockReferenceDate.$invalid && newItemSubmitted}"
                        :placeholder="constants.DATE_PLACEHOLDER_POINT_ONLY_DATE"
                        :dateFormat="constants.DATE_FORMAT_POINT"
                        :showTime="false"
                        v-model="newItem.dailyStockReferenceDate"
                        :disabled="newItem.id != null"
                        />
                    <div v-if="(v$.newItem.dailyStockReferenceDate.$invalid && newItemSubmitted) || v$.newItem.dailyStockReferenceDate.$pending.$response">
                        <small class="p-error" v-for="error in v$.newItem.dailyStockReferenceDate.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12">
                    <label :class="{'p-error':v$.newItem.idRegistryPlant.$invalid && newItemSubmitted}">{{$t('registryPlant.fk')}}</label>
                    <GenericCustomMasterDataAutocompleteV2
                        :class="{'p-invalid':v$.newItem.idRegistryPlant.$invalid && newItemSubmitted}"
                        name="masterdata-registry-plant-combo"
                        api="masterdata/registry-plant-combo"
                        v-model="newItem.idRegistryPlant" />
                    <div v-if="(v$.newItem.idRegistryPlant.$invalid && newItemSubmitted) || v$.newItem.idRegistryPlant.$pending.$response">
                        <small class="p-error" v-for="error in v$.newItem.idRegistryPlant.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12">
                    <label :class="{'p-error':v$.newItem.measureDate.$invalid && newItemSubmitted}">{{$t('StockManagementMts.measureDate')}}</label>
                    <Calendar 
                        class="w-full" 
                        :class="{'p-invalid':v$.newItem.measureDate.$invalid && newItemSubmitted}"
                        :placeholder="constants.DATE_PLACEHOLDER_POINT_ONLY_DATE"
                        :dateFormat="constants.DATE_FORMAT_POINT"
                        :showTime="true"
                        v-model="newItem.measureDate" />
                    <div v-if="(v$.newItem.measureDate.$invalid && newItemSubmitted) || v$.newItem.measureDate.$pending.$response">
                        <small class="p-error" v-for="error in v$.newItem.measureDate.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12">
                    <label>{{$t('StockManagementMts.tankCodeMts')}}</label>
                    <InputText 
                        class="w-full" 
                        type="string" 
                        v-model="newItem.tankCodeMts" 
                        :disabled="newItem.id"
                        />
                </div>
                <div class="col-12">
                    <label :class="{'p-error':v$.newItem.tankCode.$invalid && newItemSubmitted}">{{$t('StockManagementMts.tankCode')}}</label>
                    <GenericCustomAutocompleteV2
                        class="w-full" 
                        :class="{'p-invalid':v$.newItem.tankCode.$invalid && newItemSubmitted}"
                        name="masterdata-equipment-combo-type=SERB"
                        api="masterdata/equipment-combo?type=SERB"
                        v-model="newItem.tankCode"
                        :format="{ label: 'label', value: 'label' }" />
                    <div v-if="(v$.newItem.tankCode.$invalid && newItemSubmitted) || v$.newItem.tankCode.$pending.$response">
                        <small class="p-error" v-for="error in v$.newItem.tankCode.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12">
                    <label>{{$t('StockManagementMts.waterLevelMts')}}</label>
                    <InputText 
                        class="w-full" 
                        type="string" 
                        v-model="newItem.waterLevelMts" 
                        :disabled="newItem.id"
                        />
                </div>
                <div class="col-12">
                    <label :class="{'p-error':v$.newItem.waterLevel.$invalid && newItemSubmitted}">{{$t('StockManagementMts.waterLevel')}}</label>
                    <InputText 
                        class="w-full" 
                        :class="{'p-invalid':v$.newItem.waterLevel.$invalid && newItemSubmitted}"
                        type="number" 
                        v-model="newItem.waterLevel" />
                    <div v-if="(v$.newItem.waterLevel.$invalid && newItemSubmitted) || v$.newItem.waterLevel.$pending.$response">
                        <small class="p-error" v-for="error in v$.newItem.waterLevel.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12">
                    <label>{{$t('StockManagementMts.notes')}}</label>
                    <InputText 
                        class="w-full" 
                        type="string" 
                        v-model="newItem.notes" 
                        :disabled="newItem.id"
                        />
                </div>
                <div class="col-12">
                    <label>{{$t('StockManagementMts.productLevelMts')}}</label>
                    <InputText 
                        class="w-full" 
                        type="string" 
                        v-model="newItem.productLevelMts" 
                        :disabled="newItem.id"
                        />
                </div>
            </div>
                <template #footer>
                    <Button :label="$t('btn.save')" icon="pi pi-save" @click="onAddUpdateDataItem" autofocus />
                </template>
        </Dialog>

        <div class="formgrid grid">
            <div class="col-8 lg:col-2 field">
            <label for="referenceDate" :class="{'p-error':v$.filters.referenceDate.$invalid && submitted}">{{$t('StockManagementMts.referenceDate')}}</label>
            <Calendar
                :class="{'p-inputtext-sm w-full': true, 'p-invalid':v$.filters.referenceDate.$invalid && submitted}"
                :placeholder="constants.DATE_PLACEHOLDER_POINT_ONLY_DATE"
                :dateFormat="constants.DATE_FORMAT_POINT"
                :showTime="false"
                v-model="filters.referenceDate"
                :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                <div v-if="(v$.filters.referenceDate.$invalid && submitted) || v$.filters.referenceDate.$pending.$response">
                    <small class="p-error" v-for="error in v$.filters.referenceDate.$silentErrors" :key="error.$validator">
                        {{error.$message}}
                    </small>
                </div>
            </div>
            <div class="col-2 lg:col-1 field">
                <label>{{$t('at')}}</label>
                <InputText readOnly v-model="twentyFour" class="w-full " />
            </div>
            <div class="col-12 lg:col-4 my-5">
                <ResponsiveButton :label="$t('btn.load')" icon="pi pi-replay" class="mx-1 my-1" @click="loadGrid()"/>   
                <ResponsiveButton :label="$t('StockManagementMts.btn.insert')" icon="pi pi-plus" class="p-button-sm p-button-outlined m-1" @click="onAddMtsDataItemClick" :disabled="!uiPermissions('stockManagementMtsInsertButton').isEnabled"/> 
                <ResponsiveButton :label="$t('btn.reimport')" icon="pi pi-download" class="p-button-sm p-button-outlined m-1" @click="reimport" :disabled="!uiPermissions('stockManagementMtsReimportButton').isEnabled"/>
                <ResponsiveButton :label="$t('StockManagementMts.btn.export')" icon="pi pi-upload" class="p-button-sm p-button-outlined m-1" @click="exportToManualDailyStock" :disabled="!uiPermissions('stockManagementMtsExportButton').isEnabled"/>
            </div>
        </div>
        <div class="grid">
            <GenericDataTable :configuration="configuration" :category="'stockManagement-mts'" :name="'stockManagement-mts'" ref="mtsGrid" @onEditMtsDataItemClick="onEditMtsDataItemClick" :onCellDeleteComplete="loadGrid" />
        </div>
    </main>
</template>